import { IconScaleStars } from '@presentation/components/IconScaleStars';
import { InputField } from '@presentation/components/InputField';
import { PLACEHOLDER_IMAGE_USER } from '@presentation/constants';
import { useTranslation } from '@presentation/utils';
import { useFormikContext } from 'formik';
import type { FC } from 'react';
import { useMemo } from 'react';
import { FaCheckCircle } from 'react-icons/fa';
import { Skeleton } from '../Skeleton';
import * as Styled from '../styled';
import type { BreederReviewsFormValues } from '../types';
import type { ReviewItemProps } from './types';

export * from './types';
export const ReviewItem: FC<ReviewItemProps> = (props) => {
  const {
    answer,
    average,
    date,
    description,
    id,
    image,
    mode,
    name,
    onAnswerClick,
    onCancelClick,
    onEditClick,
    type,
    updateLoading,
    verified,
    canAnswerReviews,
    activeReviewId,
    loading,
  } = props;
  const { translate } = useTranslation();
  const { submitForm, values } = useFormikContext<BreederReviewsFormValues>();

  const answerContent = useMemo(() => {
    if (activeReviewId === id) {
      return values?.content;
    }
    return answer?.content;
  }, [activeReviewId, answer?.content, id, values?.content]);

  const isLoading = useMemo(() => {
    return (loading || updateLoading) && id === activeReviewId;
  }, [activeReviewId, id, loading, updateLoading]);

  const showAnswerContainer = useMemo(() => {
    return mode === 'create' && canAnswerReviews && activeReviewId === id;
  }, [activeReviewId, canAnswerReviews, id, mode]);

  const showEditInput = useMemo(() => {
    return mode === 'edit' && canAnswerReviews && activeReviewId === id;
  }, [activeReviewId, canAnswerReviews, id, mode]);

  const showEditButtons = useMemo(() => {
    return canAnswerReviews && answer;
  }, [answer, canAnswerReviews]);

  const showAnswerButton = useMemo(() => {
    return mode === 'list' && canAnswerReviews && !answer;
  }, [answer, canAnswerReviews, mode]);

  return (
    <>
      <Styled.ItemWrapper hasAnswer={!!answer}>
        <Styled.ItemImage
          src={image ?? PLACEHOLDER_IMAGE_USER}
          alt="reviewer profile image"
          // eslint-disable-next-line no-return-assign
          onError={(e) => ((e.target as any).src = PLACEHOLDER_IMAGE_USER)}
        />
        <div>
          <Styled.ItemHeader>
            <span>{name}</span>
            <span>
              <IconScaleStars amount={average ?? 0} />
            </span>
          </Styled.ItemHeader>
          <Styled.ItemDescription>{description}</Styled.ItemDescription>
          <Styled.ItemFooter>
            <span>{date}</span>
            <span>{type}</span>
            {verified && (
              <Styled.ItemVerified data-data-tooltip-content={translate('glossary.verifiedBuyer')}>
                <FaCheckCircle />
              </Styled.ItemVerified>
            )}
            {showAnswerButton && (
              <Styled.FooterButton
                onClick={() => {
                  onAnswerClick?.(id);
                }}
              >
                {translate('common.buttons.answerReview')}
              </Styled.FooterButton>
            )}
          </Styled.ItemFooter>
        </div>
      </Styled.ItemWrapper>
      {isLoading && <Skeleton />}
      {!isLoading && showAnswerContainer && (
        <Styled.AnswerWrapper>
          <Styled.ItemImage
            src={answer?.breederPic ?? PLACEHOLDER_IMAGE_USER}
            alt="reviewer profile image"
            // eslint-disable-next-line no-return-assign
            onError={(e) => ((e.target as any).src = PLACEHOLDER_IMAGE_USER)}
          />
          <Styled.AnswerCreationWrapper>
            <Styled.TextareaWrapper>
              <InputField textarea name="content" />
            </Styled.TextareaWrapper>
            <Styled.ItemFooter>
              <Styled.FooterButton onClick={submitForm} loading={isLoading} disabled={isLoading}>
                {translate('common.buttons.save')}
              </Styled.FooterButton>
              <Styled.FooterButton
                onClick={() => {
                  onCancelClick?.();
                }}
                loading={isLoading}
                disabled={isLoading}
              >
                {translate('common.buttons.cancel')}
              </Styled.FooterButton>
            </Styled.ItemFooter>
          </Styled.AnswerCreationWrapper>
        </Styled.AnswerWrapper>
      )}

      {!!answer && !isLoading && (
        <Styled.AnswerWrapper>
          <Styled.ItemImage
            src={answer.breederPic ?? PLACEHOLDER_IMAGE_USER}
            alt="breeder profile image"
            // eslint-disable-next-line no-return-assign
            onError={(e) => ((e.target as any).src = PLACEHOLDER_IMAGE_USER)}
          />
          <Styled.AnswerCreationWrapper>
            <Styled.ItemHeader>
              <span>{answer.breederName}</span>
            </Styled.ItemHeader>
            <Styled.ItemDescription>
              {showEditInput ? (
                <Styled.TextareaWrapper>
                  <InputField textarea name="content" />
                </Styled.TextareaWrapper>
              ) : (
                answerContent
              )}
            </Styled.ItemDescription>
            <Styled.ItemFooter>
              <Styled.FooterDate>{answer.date}</Styled.FooterDate>
              {showEditButtons && (
                <>
                  {mode === 'edit' ? (
                    <>
                      <Styled.FooterButton
                        onClick={submitForm}
                        loading={isLoading}
                        disabled={isLoading}
                      >
                        {translate('common.buttons.save')}
                      </Styled.FooterButton>
                      <Styled.FooterButton
                        onClick={() => {
                          onCancelClick?.();
                        }}
                        loading={isLoading}
                        disabled={isLoading}
                      >
                        {translate('common.buttons.cancel')}
                      </Styled.FooterButton>
                    </>
                  ) : (
                    <Styled.FooterButton
                      onClick={() => {
                        onEditClick?.(id);
                      }}
                    >
                      {translate('common.buttons.editAnswer')}
                    </Styled.FooterButton>
                  )}
                </>
              )}
            </Styled.ItemFooter>
          </Styled.AnswerCreationWrapper>
        </Styled.AnswerWrapper>
      )}
    </>
  );
};
